import { AnimatePresence, motion } from "motion/react";
import { X, ExternalLink, Heart } from "lucide-react";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

const APP_VERSION = "1.0.0";


interface AboutAppModalProps {
  open: boolean;
  onClose: () => void;
}

export function AboutAppModal({ open, onClose }: AboutAppModalProps) {
  const { t } = useTranslation();

  const legalInfo = [
    { label: "ООО «Название компании»", value: "" },
    { label: "ОГРН", value: "1234567890123" },
    { label: "ИНН", value: "1234567890" },
    { label: "КПП", value: "123456789" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">
                {t("aboutApp")}
              </h2>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label={t("close")}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              {legalInfo.map((item, index) => (
                <div key={index} className="flex flex-col gap-0.5">
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                  <span className="text-sm font-medium text-foreground">
                    {item.value || "—"}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
              <a
                href="https://example.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-xl px-3 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-sm font-medium text-foreground">
                  {t("termsOfUse")}
                </span>
                <ExternalLink size={16} className="text-muted-foreground" />
              </a>
              <a
                href="https://example.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-xl px-3 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-sm font-medium text-foreground">
                  {t("privacyPolicy")}
                </span>
                <ExternalLink size={16} className="text-muted-foreground" />
              </a>
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground border-t border-gray-200 dark:border-gray-700 pt-4">
              <span>Сделано с</span>
              <Heart size={12} className="text-red-400" />
              <span>от</span>
              <span className="font-medium text-foreground/80">@Y.Durov</span>
              <span>и</span>
              <span className="font-medium text-foreground/80">@G.Zaharov</span>
            </div>
            <div className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground">
                 <span className="text-xs text-muted-foreground">
                  {t("version")} {APP_VERSION}
                </span>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}